import React, { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Brand } from "../Brand";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import api from "../../lib/api";
import { toast } from "sonner";
import { CheckCircle2, AlertOctagon } from "lucide-react";

/**
 * Step 2 of password reset — consume the signed token + set a new password.
 *
 * Token comes via `?token=…` (1-hour JWT signed with kind='pwd_reset').
 * Single-use enforced server-side via `password_reset_jti`.
 */
export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  // Two audiences share the same page — the merchant variant hits a different
  // endpoint and bounces to /pay on completion, while the consumer variant
  // posts to /auth/reset-password and bounces to /login. Decided by query.
  const audience = (params.get("audience") || "user").toLowerCase();
  const isMerchant = audience === "merchant";
  const endpoint = isMerchant ? "/xpay/merchant/reset-password" : "/auth/reset-password";
  const minLen = isMerchant ? 10 : 8;
  const postLoginPath = isMerchant ? "/pay" : "/login";
  const [pwd, setPwd] = useState({ next: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token. Request a new link.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.next.length < minLen) { toast.error(`Password must be at least ${minLen} characters.`); return; }
    if (pwd.next !== pwd.confirm) { toast.error("Passwords don't match."); return; }
    setLoading(true);
    try {
      await api.post(endpoint, { token, new_password: pwd.next });
      setDone(true);
      toast.success("Password updated. Please sign in again.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed. The link may be expired or already used.");
    } finally { setLoading(false); }
  };

  return (
    <div
      className="min-h-screen bg-background flex items-start sm:items-center justify-center p-6 overflow-y-auto"
      style={{ minHeight: "100dvh", paddingBottom: "max(env(safe-area-inset-bottom), 6rem)" }}
    >
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <Brand size={48} className="mx-auto" />
        </div>
        <div className="xch-card p-6 space-y-4" data-testid="reset-password-card">
          {!token ? (
            <div className="text-center space-y-3" data-testid="reset-missing-token">
              <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-700 mx-auto flex items-center justify-center">
                <AlertOctagon className="w-6 h-6" />
              </div>
              <h2 className="font-display text-xl font-bold text-foreground">Reset link is missing</h2>
              <p className="text-sm text-muted-foreground">
                This page is opened from the link in your reset email. Request a new one if you&apos;ve lost it.
              </p>
              <Button onClick={() => navigate(isMerchant ? "/pay" : "/forgot-password")} className="bg-primary hover:bg-primary/90 text-white rounded-xl">
                {isMerchant ? "Back to Xchangely Pay" : "Request a new link"}
              </Button>
            </div>
          ) : !done ? (
            <>
              <div className="text-center">
                <h1 className="font-display text-2xl font-bold text-foreground">Set a new password</h1>
                <p className="text-sm text-muted-foreground mt-1.5">
                  Pick something only you know — at least {minLen} characters.
                </p>
              </div>
              <form onSubmit={submit} className="space-y-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">New password</Label>
                  <Input required type="password" minLength={minLen} value={pwd.next}
                         autoComplete="new-password"
                         onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                         className="rounded-xl"
                         data-testid="reset-new-pwd-input" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Confirm new password</Label>
                  <Input required type="password" minLength={minLen} value={pwd.confirm}
                         autoComplete="new-password"
                         onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                         className="rounded-xl"
                         data-testid="reset-confirm-pwd-input" />
                </div>
                <Button type="submit" disabled={loading || pwd.next.length < minLen || pwd.next !== pwd.confirm}
                        className="w-full bg-primary hover:bg-primary/90 text-white rounded-xl h-12 font-display font-semibold"
                        data-testid="reset-submit-btn">
                  {loading ? "Updating…" : "Update password"}
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center space-y-3" data-testid="reset-success">
              <div className="w-14 h-14 rounded-2xl bg-emerald-50 text-emerald-700 mx-auto flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h2 className="font-display text-xl font-bold text-foreground">Password updated</h2>
              <p className="text-sm text-muted-foreground">
                You&apos;ll need to sign in again with your new password. Your saved devices have been cleared as a security precaution.
              </p>
              <Button onClick={() => navigate(postLoginPath)} className="bg-primary hover:bg-primary/90 text-white rounded-xl">
                {isMerchant ? "Sign in to Xchangely Pay" : "Continue to sign in"}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
